import { Link } from "react-router-dom";
import { PageHero, PageLayout } from "../components/PageShell";
import { Figure } from "../components/Figure";
import { useSectionTabs } from "../lib/useSectionTabs";
import { ServiceWingCard } from "./ServiceWing";
import { ROUTES } from "../lib/routes";
import {
  SAC,
  DAVANA,
  SERVICE_WINGS,
  GREEN_CAMPUS,
  CELLS,
  CULTURAL_CENTRE,
  AMENITIES,
  SPORTS,
  PUBLICATIONS,
  GRADUATIONS,
  GALLERY,
  CAMPUS_LIFE_HERO,
  unspent,
  personPhoto,
  type Amenity,
  type Person,
} from "../lib/campusLife";

const SECTIONS = [
  { id: "amenities", label: "Amenities" },
  { id: "council", label: "Student council" },
  { id: "davana", label: "Davana" },
  { id: "graduation", label: "Graduation Day" },
  { id: "service", label: "Service wings" },
  { id: "sport", label: "Sport" },
  { id: "innovation", label: "Innovation" },
  { id: "cultural-centre", label: "Cultural centre" },
  { id: "publications", label: "Publications" },
  { id: "sustainability", label: "Sustainability" },
];

/**
 * One "What's on campus" card. A facility that has a page of its own on this
 * site renders as a link; the rest stay plain divs, so nothing looks clickable
 * that isn't.
 */
function AmenityCard({ a }: { a: Amenity }) {
  const photo = unspent(a.photos)[0];
  const inner = (
    <>
      {photo && (
        <Figure
          media={photo}
          fallbackLabel={a.name.toUpperCase()}
          aspect="3/2"
          sizes="(max-width: 640px) 100vw, (max-width: 1000px) 48vw, 31vw"
        />
      )}
      <div className="fac-body">
        <h3>{a.name}</h3>
        <p className="fac-strap">{a.strap}</p>
        {a.to && (
          <span className="fac-more">
            Read more <span className="arr">→</span>
          </span>
        )}
      </div>
    </>
  );

  return a.to ? (
    <Link to={a.to} className="fac-card reveal">
      {inner}
    </Link>
  ) : (
    <div className="fac-card reveal">{inner}</div>
  );
}

/** Initials for a member with no portrait, honorifics stripped. */
function initials(name: string): string {
  const parts = name
    .replace(/^(Prof\.?|Dr\.?|Sri|Smt\.?|Mr\.?|Ms\.?|Mrs\.?)\s+/i, "")
    .split(/\s+/)
    .filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

/**
 * The council's office as a table — office, member, designation — matching the
 * BEA governing council on /about. The office cell spans its members, so a role
 * held by two people reads as one entry rather than a repeated label.
 *
 * Portraits are borrowed from the staff directory by slug, and only where the
 * name matched exactly; the two members without a match fall back to initials
 * rather than risk the wrong face against a real name.
 */
function CouncilRoster({ offices }: { offices: Array<{ title: string; people: Person[] }> }) {
  return (
    <div className="data-table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">Office</th>
            <th scope="col">Member</th>
            <th scope="col">Designation</th>
          </tr>
        </thead>
        <tbody>
          {offices.flatMap((o) =>
            o.people.map((p, i) => {
              const photo = personPhoto(p);
              return (
                <tr key={`${o.title}-${p.name}`}>
                  {i === 0 && (
                    <th scope="rowgroup" rowSpan={o.people.length} className="cr-office">
                      {o.title}
                    </th>
                  )}
                  <td className="cr-name">
                    <span className="cr-person">
                      {photo ? (
                        <Figure
                          media={photo}
                          fallbackLabel={p.name}
                          aspect="1/1"
                          className="cr-avatar"
                          sizes="40px"
                        />
                      ) : (
                        <span className="cr-avatar cr-avatar--initials" aria-hidden>
                          {initials(p.name)}
                        </span>
                      )}
                      <span>{p.name}</span>
                    </span>
                  </td>
                  <td className="cr-role">{p.role}</td>
                </tr>
              );
            }),
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function CampusLife() {
  const activeId = useSectionTabs(SECTIONS.map((s) => s.id));
  return (
    <>
      <PageHero
        variant="hero-photo"
        eyebrow="Campus Life"
        title="More than four years of learning."
        lead="A students' council that has run its own festivals since 1984, three service wings, a 700-seat cultural centre, and the labs, hostels and playing fields around them — on the Shamanur Road campus in Davanagere."
        crumbs={[{ label: "Campus Life" }]}
        heroMedia={CAMPUS_LIFE_HERO}
        image="S. S. MALLIKARJUN CULTURAL CENTRE"
      />

      <div className="page-tabs">
      <PageLayout
        side={[
          {
            title: "On this page",
            items: SECTIONS.map((s) => ({ label: s.label, to: `#${s.id}`, active: activeId === s.id })),
          },
        ]}
      >
        {/* ---------------------------------------------------------------- */}
        <section className="section" id="amenities">
          <div className="section-head">
            <div>
              <div className="kicker">Amenities</div>
              <h2>What's on campus</h2>
            </div>
          </div>
          <div className="fac-grid">
            {AMENITIES.map((a) => (
              <AmenityCard key={a.slug} a={a} />
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        <section className="section" id="council">
          <div className="section-head">
            <div>
              <div className="kicker">Student council · since {SAC.since}</div>
              <h2>{SAC.name}</h2>
            </div>
          </div>
          {SAC.intro.map((p, i) => (
            <p key={i} className={i === 0 ? "lede" : undefined} style={{ marginTop: 14 }}>
              {p}
            </p>
          ))}

          <div className="dept-grid" style={{ marginTop: 32 }}>
            {SAC.activities.map((a, i) => (
              <div key={a.title} className="dept-card reveal">
                <div className="dept-code">{String(i + 1).padStart(2, "0")}</div>
                <h3>{a.title}</h3>
                <p>{a.detail}</p>
              </div>
            ))}
          </div>

          <p style={{ marginTop: 28, color: "var(--text-soft)" }}>{SAC.outward}</p>

          {unspent(SAC.photos).length > 0 && (
            <div className="venue-grid venue-grid--3">
              {unspent(SAC.photos).map((p) => (
                <figure key={p.url} className="venue-card reveal">
                  <Figure media={p} fallbackLabel="SAC" aspect="3/2" sizes="(max-width: 700px) 100vw, 32vw" />
                </figure>
              ))}
            </div>
          )}

          <div className="kicker" style={{ marginTop: 48 }}>
            The council
          </div>
          <CouncilRoster
            offices={[
              { title: "President", people: SAC.office.president },
              { title: "Dean, SAC", people: SAC.office.dean },
              { title: "Advisors", people: SAC.office.advisors },
              { title: "Conveners", people: SAC.office.conveners },
              { title: "Advisory committee", people: SAC.office.advisory },
            ]}
          />
        </section>

        {/* ---------------------------------------------------------------- */}
        <section className="section" id="davana">
          <div className="section-head">
            <div>
              <div className="kicker">Cultural festival</div>
              <h2>{DAVANA.name}</h2>
            </div>
          </div>
          <p className="lede">{DAVANA.blurb}</p>

          <div className="dv-card reveal">
            <Figure
              media={DAVANA.photo}
              fallbackLabel="DAVANA · CULTURAL FESTIVAL"
              aspect="auto"
              className="dv-card-media"
              sizes="(max-width: 760px) 100vw, 46vw"
            />
            <div className="dv-card-body">
              <div className="dv-card-edition">{DAVANA.edition}</div>
              <div className="dv-facts">
                <div>
                  <span className="dv-fact-label">When</span>
                  <span className="dv-fact-value">{DAVANA.dates}</span>
                </div>
                <div>
                  <span className="dv-fact-label">Where</span>
                  <span className="dv-fact-value">{DAVANA.venue}</span>
                </div>
                <div>
                  <span className="dv-fact-label">Disciplines</span>
                  <span className="dv-fact-value">
                    {DAVANA.disciplines.map((d) => d.name).join(" · ")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="kicker" style={{ marginTop: 40 }}>
            Six disciplines
          </div>
          <div className="dept-grid" style={{ marginTop: 14 }}>
            {DAVANA.disciplines.map((d) => (
              <div key={d.name} className="dept-card reveal">
                <div className="dept-code">{d.events.length} events</div>
                <h3>{d.name}</h3>
                <ul className="detail-list" style={{ marginTop: 10, marginBottom: 0 }}>
                  {d.events.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        <section className="section" id="graduation">
          <div className="section-head">
            <div>
              <div className="kicker">Ceremony</div>
              <h2>Graduation Day</h2>
            </div>
          </div>
          <p className="lede">
            Graduation Day has run since the institute's earliest cohorts — the most recent was its
            41st. These are the {GRADUATIONS.length} the institute publishes a record for, with a
            photograph album for each.
          </p>
          <ul className="contact-rows" style={{ marginTop: 28 }}>
            {GRADUATIONS.map((g) => (
              <li key={g.ordinal} className="contact-row">
                <div>
                  <div className="contact-row-role">{g.date}</div>
                  <div className="contact-row-name">{g.ordinal}</div>
                </div>
                {g.album && (
                  <div className="contact-row-reach">
                    <a href={g.album} target="_blank" rel="noreferrer">
                      Photographs →
                    </a>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>

        {/* ---------------------------------------------------------------- */}
        <section className="section" id="service">
          <div className="section-head">
            <div>
              <div className="kicker">Service</div>
              <h2>Three wings, one obligation</h2>
            </div>
          </div>
          <p className="lede">
            Every BIET student can join the National Service Scheme, the National Cadet Corps or the
            Youth Red Cross Wing. Each is led by a faculty programme officer and answers to a
            national body outside the institute.
          </p>

          <div className="sw-grid">
            {SERVICE_WINGS.map((w) => (
              <ServiceWingCard key={w.slug} w={w} />
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        <section className="section" id="sport">
          <div className="section-head">
            <div>
              <div className="kicker">Sport</div>
              <h2>Playing for the institute</h2>
            </div>
          </div>
          {SPORTS.intro.map((p, i) => (
            <p key={i} className="lede">
              {p}
            </p>
          ))}

          {unspent(SPORTS.photos).length > 0 && (
            <div className="venue-grid">
              {unspent(SPORTS.photos).slice(0, 9).map((p) => (
                <figure key={p.url} className="venue-card reveal">
                  <Figure
                    media={p}
                    fallbackLabel="SPORTS"
                    aspect="3/2"
                    sizes="(max-width: 700px) 100vw, 30vw"
                  />
                  {p.alt && <figcaption><span className="venue-caption">{p.alt}</span></figcaption>}
                </figure>
              ))}
            </div>
          )}

          <p style={{ marginTop: 28, color: "var(--text-muted)", fontSize: 14 }}>
            The institute publishes an annual sports report; editions exist for{" "}
            {SPORTS.reportYears.join(", ")}.
          </p>
        </section>

        {/* ---------------------------------------------------------------- */}
        <section className="section" id="innovation">
          <div className="section-head">
            <div>
              <div className="kicker">Innovation & enterprise</div>
              <h2>Four ways to build something</h2>
            </div>
          </div>
          <div className="fac-grid">
            {CELLS.map((c) => (
              <div key={c.slug} className="fac-card reveal">
                <div className="fac-body">
                  <div className="dept-code">{c.abbr ?? "CELL"}</div>
                  <h3>{c.name}</h3>
                  <p className="fac-strap">{c.strap}</p>
                  {c.meta && <div className="fac-meta">{c.meta}</div>}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        <section className="section" id="cultural-centre">
          <div className="section-head">
            <div>
              <div className="kicker">Venue</div>
              <h2>{CULTURAL_CENTRE.name}</h2>
            </div>
          </div>
          {CULTURAL_CENTRE.intro.map((p, i) => (
            <p key={i} className={i === 0 ? "lede" : undefined} style={{ marginTop: i ? 14 : 0 }}>
              {p}
            </p>
          ))}

          <div className="facility-grid">
            {CULTURAL_CENTRE.facts.map((f) => (
              <div key={f.label} className="facility-cell reveal">
                <div className="facility-num" style={{ fontSize: 22 }}>{f.value}</div>
                <div className="facility-label">{f.label}</div>
              </div>
            ))}
          </div>

          <div className="club-leadership" style={{ marginTop: 28 }}>
            {CULTURAL_CENTRE.systems.map((s) => (
              <div key={s.what} className="cl-cell">
                <div className="cl-role">{s.what}</div>
                <div className="cl-name">{s.who}</div>
              </div>
            ))}
          </div>

          {unspent(CULTURAL_CENTRE.photos).length > 0 && (
            <div className="venue-grid">
              {unspent(CULTURAL_CENTRE.photos).slice(0, 6).map((p) => (
                <figure key={p.url} className="venue-card reveal">
                  <Figure
                    media={p}
                    fallbackLabel="SSM CULTURAL CENTRE"
                    aspect="3/2"
                    sizes="(max-width: 700px) 100vw, 30vw"
                  />
                </figure>
              ))}
            </div>
          )}
        </section>


        {/* ---------------------------------------------------------------- */}
        <section className="section" id="publications">
          <div className="section-head">
            <div>
              <div className="kicker">In print</div>
              <h2>Magazines and ceremony</h2>
            </div>
          </div>
          <div className="dept-grid">
            {PUBLICATIONS.map((p) => (
              <div key={p.name} className="dept-card reveal">
                <div className="dept-code">{p.strap}</div>
                <h3>{p.name}</h3>
                <p>{p.detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        <section className="section" id="sustainability">
          <div className="section-head">
            <div>
              <div className="kicker">Sustainability</div>
              <h2>{GREEN_CAMPUS.name}</h2>
            </div>
          </div>
          <div className="xsec">
            <div>
              <p className="xsec-lead">{GREEN_CAMPUS.intro[1]}</p>
              <p className="xsec-lead">
                Every engineering department files its own BIGc report each year, so the programme
                is audited department by department rather than centrally.
              </p>
            </div>
            <div className="xsec-meta">
              <div className="xsec-meta-row">
                <span className="xsec-meta-label">Known as</span>
                <span className="xsec-meta-value">
                  {GREEN_CAMPUS.abbr} — {GREEN_CAMPUS.strap}
                </span>
              </div>
              <div className="xsec-meta-row">
                <span className="xsec-meta-label">Chairman</span>
                <span className="xsec-meta-value">{GREEN_CAMPUS.chairman.name}</span>
                <span className="xsec-meta-note">{GREEN_CAMPUS.chairman.role}</span>
              </div>
              <div className="xsec-meta-row">
                <span className="xsec-meta-label">Coordinator</span>
                <span className="xsec-meta-value">{GREEN_CAMPUS.coordinator.name}</span>
                <span className="xsec-meta-note">{GREEN_CAMPUS.coordinator.role}</span>
              </div>
              <div className="xsec-meta-row">
                <span className="xsec-meta-label">Reporting departments</span>
                <span className="xsec-meta-value">{GREEN_CAMPUS.reportingDepartments.length}</span>
              </div>
            </div>
          </div>
        </section>

      </PageLayout>
      </div>

      {GALLERY.length > 0 && (
        <section className="photo-essay reveal">
          <div className="photo-essay-head">
            <div>
              <div className="kicker">Photo essay</div>
              <h2>A walk across campus.</h2>
            </div>
            <div className="photo-essay-counter">
              <span className="current">01</span>
              <span className="sep"> / </span>
              <span className="total">{String(GALLERY.length).padStart(2, "0")}</span>
            </div>
          </div>
          <div
            className="photo-essay-track"
            ref={(el: HTMLDivElement | null) => {
              if (!el || el.dataset._wired) return;
              el.dataset._wired = "1";
              const slides = el.querySelectorAll<HTMLElement>(".photo-essay-slide");
              const counter = el.parentElement?.querySelector<HTMLElement>(
                ".photo-essay-counter .current",
              );
              if (!counter) return;
              const update = () => {
                const r = el.getBoundingClientRect();
                const center = r.left + r.width * 0.4;
                let bestIdx = 0;
                let bestDist = Infinity;
                slides.forEach((s, i) => {
                  const sr = s.getBoundingClientRect();
                  const d = Math.abs(sr.left + sr.width / 2 - center);
                  if (d < bestDist) {
                    bestDist = d;
                    bestIdx = i;
                  }
                });
                counter.textContent = String(bestIdx + 1).padStart(2, "0");
              };
              el.addEventListener("scroll", update, { passive: true });
              setTimeout(update, 200);
            }}
          >
            {GALLERY.map((s, i) => (
              <div key={s.media.url} className="photo-essay-slide">
                <Figure
                  media={s.media}
                  fallbackLabel={s.title.toUpperCase()}
                  aspect="16/10"
                  kind="dark"
                  sizes="(max-width: 900px) 90vw, 720px"
                />
                <div className="photo-essay-slide-caption">
                  <span className="num">{String(i + 1).padStart(2, "0")}</span>
                  <h4>{s.title}</h4>
                  <p>{s.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
